import '../style.css'
import * as THREE from 'three';
import * as utils from './utilities/utils';
import CameraManager from './managers/CameraManager';
import RendererManager from './managers/RendererManager';
import ControlsManager from './managers/ControlsManger';
import { ACUNIT_OBSTRUCTION, CAMERA_TYPE_2D, MUMTY_OBSTRUCTION } from './utilities/constants';
import SceneManager from './managers/SceneManager';
import Ground from './Objects/Ground/Ground';
import LightsManager from './managers/LightsManager';
import VisualManager from './managers/VisualManager';
import SnapshotCapture from './utilities/snapshotCapture';
import DrawManager from './managers/DrawManager';
import PolygonModel from './Objects/PolygonModel';
import GroundImage from './Objects/Ground/GroundImage';
import { DragControls } from './libs/DragControls';
import Obstruction from './Objects/Obstruction';

import { useStudioStore } from '../pages/store/studioStore';
import { useContactDataStore } from '../pages/store/contactDataStore';
import { isIntersecting } from './utilities/rayCastingUtils';
import { DRAW_OBSTRUCTION } from '../pages/studio/constants';
import API from '@/services/api';
import HeatSolar from './Objects/Ground/HeatSolar';
import { COLOR_MAPPINGS, MATERIAL_STATES, VISUAL_STATES } from './utilities/visualConstants';
import chroma from "chroma-js";
import { ElMessage } from 'element-plus';
import { GOOGLE_API_KEY } from '../constants';
import studioDataService from '../services/studioDataService';
import appMode from '../services/appMode';
import { postToRN } from '../utils/rnBridge';
import { ensureSasToken, uploadSnapshots } from '../services/snapshotBlobService';
export default class App {
    constructor() {
        /*replace event bus with store*/
        window.stage = this;
        this.eventBus = new THREE.EventDispatcher();
        // Lightweight event manager to surface notifications in the UI and avoid
        // no-op calls from DrawManager/Objects when running in the portal.
        this.eventManager = this._buildEventManager();
        this.designId = -1;
        this.objects = {};
        this.canvas = document.getElementById("design-canvas");
        this.sceneRotated = false;
        this.studioStore = useStudioStore();
        this.currentSelectedObject = null;
        this.obs = [];
        this.obstructionPositionArray = [];
        this.lastChangedObject = [];
        this.redoStack = [];
        // Global gate for whether obstructions are selectable via DragControls/raycasting
        // Disabled by default; UI enables it only in OBSTRUCTION_STATE
        this.obstructionSelectionEnabled = false;

        let panelDefaultColor =
            COLOR_MAPPINGS.PANEL[MATERIAL_STATES.TRANSLUCENT][VISUAL_STATES.DEFAULT_STATES.DEFAULT].MESH_COLOR.toString(16);
        for (let i = 0; i <= 6 - panelDefaultColor.length; i += 1) {
            panelDefaultColor = "0" + panelDefaultColor;
        }
        panelDefaultColor = "#" + panelDefaultColor;

        let solarAccessColors = [
            panelDefaultColor,
            "#d73027",
            "#f46d43",
            "#fdae61",
            "#fee08b",
            "#ffffbf",
            "#d9ef8b",
            "#a6d96a",
            "#66bd63",
            "#1a9850"
        ];
        this.solarAccessColorMap = chroma
            .scale(solarAccessColors)
            .domain([0, 0.01, 0.45, 0.5, 0.55, 0.6, 0.75, 0.8, 0.9, 1.0])
            .gamma(3);

        this.lengthVisible = true;
        this.arcVisible = true;

        this.cameraUpdates = [];

        // App-mode panel overrides (populated from contactDataStore during loadStage,
        // then overwritten with user-selected panel/inverter from PANEL_SETTINGS_STATE).
        // Subarray.getFixedMountProperties() reads these when non-null.
        this.appPanelConfig = null;   // { moduleProperties, panelProperties, tilt }
        this.appTargetPanels = null;  // target n_panels from report_json
    }

    _buildEventManager() {
        const safeMessage = (opts) => {
            try { ElMessage({ showClose: true, grouping: true, duration: 2500, ...opts }); }
            catch (_) { /* ignore if ElMessage not available yet */ }
        };
        return {
            // Generic toast error
            customErrorMessage: (message, title) => safeMessage({ message: message || 'Something went wrong', type: 'error' }),

            // Loading notifications used by polygon/subarray operations
            setPolygonModelLoading: () => { safeMessage({ message: 'Updating roof...', type: 'info', duration: 0 }); return { close(){} } },
            completePolygonModelLoading: (n) => { try { n?.close?.(); } catch(_) {} },
            setUpdatePanelPlacementLoading: () => { safeMessage({ message: 'Updating panel placement...', type: 'info', duration: 0 }); return { close(){} } },
            completeUpdatePanelPlacementLoading: (n) => { try { n?.close?.(); } catch(_) {} },

            // No-ops for studio top bar hooks in this lightweight portal integration
            setTopBarWhileDrawing: () => Promise.resolve(),
            setObjectsSelected: () => {},
            addTableMode: () => {},

            // Misc helpers used sparsely
            dcCapSizeExceeded: () => safeMessage({ message: 'DC cap size exceeded', type: 'warning' }),
            subarrayEmptyError: () => safeMessage({ message: 'No panels placed', type: 'warning' }),
        };
    }

    async loadStage(canvas, latitude, longitude, zoom, designSettings, mapImage, stageData) {
        /* Adding an event listener to the window object. */
        this.canvas = canvas;
        this.latitude = latitude;
        this.longitude = longitude;
        this.zoom = zoom;
        this.designSettings = designSettings;
        this.mapImage = mapImage;
        this.loadMapData();

        // In app mode, load panel overrides from contactDataStore (report_json)
        if (appMode.isAppMode()) {
            const contactStore = useContactDataStore();
            if (contactStore.loaded) {
                this.appPanelConfig  = contactStore.subarrayPanelConfig;
                this.appTargetPanels = contactStore.nPanels;
            }
        }

        this.imageDimensions = utils.getImageDimensions(this.latitude, this.longitude, this.zoom, 512, 512);
        this.screenDimensions = {
            width: this.canvas.width,
            height: this.canvas.height
        };

        /* Creating a new instance of the Managers. */
        this.sceneManager = new SceneManager();
        this.cameraManager = new CameraManager(this.screenDimensions, this.imageDimensions, CAMERA_TYPE_2D);
        this.rendererManager = new RendererManager(this.canvas);
        this.cameraManager.get2dCamera().y = 0;
        this.floorValue = stageData.floorValue ? stageData.floorValue : 1;
        this.ground = new Ground(this);
        //this.switchTo2d();
        this.fitCameraToObject();
        this.lightsManager = new LightsManager(this);
        this.drawManager = new DrawManager(this);
        this.visualManager = new VisualManager(this);

        // Initialize snapshot capture utility
        // Pass 'this' as 4th argument so SnapshotCapture can read sceneRotated state
        this.snapshotCapture = new SnapshotCapture(
            this.rendererManager.renderer,
            this.sceneManager.scene,
            this.cameraManager,
            this
        );

        // Initialize ID counters BEFORE ground loading so that Subarray/PolygonModel
        // constructors get valid IDs during loadObject() instead of NaN.
        this._SUBARRAY_ID_INIT =
            Object.prototype.hasOwnProperty.call(stageData, '_SUBARRAY_ID_INIT') ?
                stageData._SUBARRAY_ID_INIT :
                0;
        this._MODEL_ID_INIT =
            Object.prototype.hasOwnProperty.call(stageData, 'modelId') ?
                stageData.modelId :
                0;
        this._OBS_ID_INIT =
            Object.prototype.hasOwnProperty.call(stageData, 'obsId') ?
                stageData.obsId :
                0;

        // Load ground data first (this creates polygonModel and restores subarrays/panels)
        try {
            await this.ground.loadObject(stageData.groundData);
        } catch (groundErr) {
            console.error('[App.loadStage] ground.loadObject failed — continuing without polygons:', groundErr);
        }

        // Load obstructions after ground is loaded (requires polygonModel to exist)
        this.loadObstructions(stageData.obstructionData);
        this.dragControls = new DragControls(this, this.cameraManager.camera, this.rendererManager.renderer.domElement);

        this.heatSolar = new HeatSolar(this);
        if (utils.isTouchDevice()) {
            this.lengthVisible = false;
            this.arcVisible = false;
            this.dragControls.deactivate();
        }
        this.obstructionInScene();
        if (!(utils.isTouchDevice())) {
            this.initialize2D_3DControls();
            this.controlsManager.use2dControls();
        }
    }

    loadMapData() {
        let mapInfo;
        const fallbackLat  = 37.4200952208586900;
        const fallbackLng  = -122.0822690926250400;
        const fallbackZoom = 20;

        if (appMode.isAppMode()) {
            // APP MODE: location from contactDataStore (populated from API or fallback)
            const contactStore = useContactDataStore();

            // Primary: contactStore (populated in studio.vue for both old & new contacts)
            // Fallback: constructor params (this.latitude / this.longitude from loadStage call)
            const lat  = contactStore.latitude  ?? this.latitude  ?? fallbackLat;
            const lng  = contactStore.longitude ?? this.longitude ?? fallbackLng;
            const zoom = contactStore.zoom ?? this.zoom ?? fallbackZoom;

            if (lat == null || lng == null) {
                throw new Error('Missing location data from API. Contact API must provide latitude and longitude in input_details.');
            }

            // Build Google Static Maps URL from API coordinates
            const builtUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&scale=2&zoom=${zoom}&maptype=satellite&size=512x512&key=${GOOGLE_API_KEY}`;

            // Check if we have a saved image URL in design_details that matches these coordinates
            const stored = studioDataService.getMapData() || {};
            const storedLat  = stored.lat  ?? stored.latitude  ?? null;
            const storedLng  = stored.lng  ?? stored.longitude ?? null;
            const coordsMatch =
                storedLat != null && storedLng != null &&
                Math.abs(storedLat - lat) < 0.0001 && Math.abs(storedLng - lng) < 0.0001;
            const storedImageUrl = stored.groundImage || stored.url || stored.imageUrl || null;
            const imageUrl = (coordsMatch && storedImageUrl) ? storedImageUrl : builtUrl;

            mapInfo = { lat, lng, zoom, url: imageUrl, groundImage: imageUrl };

            // Persist resolved mapData back to studioDataService so saveDesign()
            // includes the correct imageUrl and coordinates for this session
            studioDataService.setMapData({
                ...(studioDataService.getMapData() || {}),
                latitude: lat,
                longitude: lng,
                zoom,
                imageUrl,
            });
        } else {
            // WEB MODE: read map info from localStorage (original dev behavior)
            mapInfo = JSON.parse(localStorage.getItem('google-map-info'));
            if (!mapInfo) {
                const builtUrl = `https://maps.googleapis.com/maps/api/staticmap?center=${fallbackLat},${fallbackLng}&scale=2&zoom=${fallbackZoom}&maptype=satellite&size=512x512&key=${GOOGLE_API_KEY}`;
                mapInfo = { url: builtUrl, lat: fallbackLat, lng: fallbackLng, zoom: fallbackZoom };
            }
        }

        this.groundImage = mapInfo.groundImage || mapInfo.url;
        this.latitude = mapInfo.lat;
        this.longitude = mapInfo.lng;
        this.zoom = mapInfo.zoom;
        return mapInfo;
        }

    saveMapManual() {
        let mapImageURL = `https://maps.googleapis.com/maps/api/staticmap?center=37.4200952208586900,-122.0822690926250400&scale=2&zoom=20&maptype=satellite&size=512x512&key=${GOOGLE_API_KEY}`;
        let mapInfo = {
            "url": mapImageURL,
            "lat": 37.4200952208586900,
            'lng': -122.0822690926250400,
            "zoom": 20
        }
        studioDataService.setMapData(mapInfo);
    }

    loadObstructions(obstructionData) {
        if (!obstructionData) {
            obstructionData = [];
        }

        // Clear existing obstructions from stage before loading new ones
        if (this.obs && this.obs.length > 0) {
            this.obs = [];
            this.obstructionPositionArray = [];
        }

        if (appMode.isAppMode()) {
            // APP MODE: persist obstruction list into studioDataService
            let stageData = studioDataService.getStageData();
            stageData.obstructionData = obstructionData;
            studioDataService.setStageData(stageData);
        } else {
            // WEB MODE: persist obstruction list into localStorage (original dev behavior)
            if (!obstructionData.length) {
                let stageData = JSON.parse(localStorage.getItem('stageData'));
                if (stageData) {
                    stageData.obstructionData = obstructionData;
                    localStorage.setItem('stageData', JSON.stringify(stageData));
                }
            }
        }

        // Load obstructions into the scene (both modes)
        obstructionData.forEach(obs => {
            const obsItem = new Obstruction(this, obs.coreHeight, obs.type, obs.id);
            obsItem.loadObject(obs);
        });
    }

    initialize2D_3DControls() {
        // Dispose previous controls so their event listeners are removed from the DOM.
        // Without this, every call stacks new touchstart/touchmove/touchend listeners
        // on the canvas, which consume/block all touch events.
        if (this.controlsManager) {
            if (this.controlsManager._2dControls) this.controlsManager._2dControls.dispose();
            if (this.controlsManager._3dControls) this.controlsManager._3dControls.dispose();
        }

        this.controlsManager = new ControlsManager(
            this.cameraManager.get2dCamera(),
            this.cameraManager.get3dCamera(),
            this.canvas.parentElement,
            this.eventBus,
            this
        );

        // Only create LightsManager once — it adds groups to the scene
        if (!this.lightsManager) {
            this.lightsManager = new LightsManager(this);
        }

        this.cameraManager.get2dCamera().y = 0;

        this.switchTo2d();
        this.fitCameraToObject();
    }

    animate() {
        for (let i = 0; i < this.cameraUpdates.length; i++) {
            let func = this.cameraUpdates[i];
            if (func !== undefined) {
                func();
            }
        }
        this.cameraUpdates = [];
        this.animationId = requestAnimationFrame(this.animate.bind(this));
        // this.cameraManager.perspectiveCamera.lookAt(0,0,0);
        // this.cameraManager.perspectiveCamera.updateProjectionMatrix();
        this.rendererManager.render(this.sceneManager.scene, this.cameraManager.camera);
        //this.updateCameraPosition();
    }

    obstructionScaled(scaleValue = 1) {
        if (scaleValue === 0) {
            scaleValue = 1;
        }
        if (this.currentSelectedObject instanceof Obstruction)
            this.storeScaleOfLastChangedObject(this.currentSelectedObject);
        if (this.currentSelectedObject instanceof Obstruction) this.currentSelectedObject.updateObstructionScale(scaleValue);
    }

    obstructionRotated(rotationValue) {
        if (this.currentSelectedObject instanceof Obstruction) {
            this.storeAngleOfLastChangedObject(this.currentSelectedObject);
            this.currentSelectedObject.rotateObstruction(rotationValue);
            this.redoStack = [];
        }
    }

    storePositionOfLastChangedObject(object, position) {
        try {
            const curr = object?.getPosition ? object.getPosition() : null;
            // Skip if no actual movement
            if (curr && position && Math.abs(curr.x - position.x) < 1e-6 && Math.abs(curr.y - position.y) < 1e-6 && Math.abs((curr.z || 0) - (position.z || 0)) < 1e-6) {
                return;
            }
        } catch(_) { /* ignore */ }
        this.lastChangedObject.push([object, 'pos']);
        object.storeLastPosition({ ...position });
        this.redoStack = [];
        this.studioStore.$patch({ 
            undoEnabled: this.lastChangedObject.length > 0,
            redoEnabled: false 
        });
    }
    storeScaleOfLastChangedObject(object) {
        // Only push to history if scale actually changes to avoid empty state
        const prev = object?.objectLastScale?.length ? object.objectLastScale[object.objectLastScale.length - 1] : null;
        if (prev === null || prev !== object.scale) {
            this.lastChangedObject.push([object, 'scale']);
            object.storeLastScale();
            this.redoStack = [];
            this.studioStore.$patch({ 
                undoEnabled: this.lastChangedObject.length > 0,
                redoEnabled: false 
            });
        }
    }

    storeAngleOfLastChangedObject(object) {
        const prev = object?.objectLastAngle?.length ? object.objectLastAngle[object.objectLastAngle.length - 1] : null;
        if (prev === null || prev !== object.angle) {
            this.lastChangedObject.push([object, 'angle']);
            object.storeLastAngle();
            this.redoStack = [];
            this.studioStore.$patch({ 
                undoEnabled: this.lastChangedObject.length > 0,
                redoEnabled: false 
            });
        }
    }

    obstructionInScene() {
        if (this.ground.hasObstacles()) {
            this.studioStore.$patch({ undoEnabled: true });
            this.studioStore.$patch({ obstructionPlacedInScene: true });
        } else {
            this.studioStore.$patch({ undoEnabled: false });
            this.studioStore.$patch({ obstructionPlacedInScene: false });
        }
    }

    handleUndo() {
        if (this.lastChangedObject.length > 0) {
            let undoneEntry = this.lastChangedObject.pop();
            let obj = undoneEntry[0];
            let type = undoneEntry[1];

            // Before undoing, capture state for redo
            if (type === 'pos') {
                // If this is the initial create (no prior positions/scales/angles), store a recreate snapshot
                const isInitialCreate = (obj?.objectLastPosition?.length <= 1) && (obj?.objectLastScale?.length === 0) && (obj?.objectLastAngle?.length === 0);
                if (isInitialCreate && typeof obj.saveObject === 'function') {
                    const snapshot = obj.saveObject();
                    this.redoStack.push(['recreate', snapshot]);
                } else {
                    this.redoStack.push([obj, 'pos', obj.getPosition()]);
                }
            } else if (type === 'scale') {
                this.redoStack.push([obj, 'scale', obj.scale]);
            } else if (type === 'angle') {
                this.redoStack.push([obj, 'angle', obj.angle]);
            }

            obj.undo(type);
        }
        this.studioStore.$patch({ 
            undoEnabled: this.lastChangedObject.length > 0,
            redoEnabled: this.redoStack.length > 0 
        });
    }

    handleRedo() {
        if (this.redoStack.length > 0) {
            let redoEntry = this.redoStack.pop();
            // Support recreate entries for objects undone from initial creation
            if (typeof redoEntry[0] === 'string' && redoEntry[0] === 'recreate') {
                const snapshot = redoEntry[1];
                try {
                    const recreated = new Obstruction(this, snapshot.coreHeight || 3, snapshot.type, snapshot.id);
                    // loadObject will set geometry/parent/position/scale/angle
                    recreated.loadObject(snapshot);
                    // Register creation in history so an immediate undo removes it again
                    this.lastChangedObject.push([recreated, 'pos']);
                    recreated.storeLastPosition(recreated.getPosition());
                    // Retarget any remaining redo entries that referenced the old
                    // obstruction instance to point to the newly recreated one,
                    // matching by stable domain id.
                    for (let i = 0; i < this.redoStack.length; i++) {
                        const e = this.redoStack[i];
                        if (e && typeof e[0] === 'object' && e[0]?.id === snapshot.id) {
                            this.redoStack[i] = [recreated, e[1], e[2]];
                        }
                    }
                } catch (e) {
                    console.error('[APP] Failed to redo create obstruction:', e);
                }
                this.studioStore.$patch({ 
                    undoEnabled: this.lastChangedObject.length > 0,
                    redoEnabled: this.redoStack.length > 0 
                });
                return;
            }

            let obj = redoEntry[0];
            let type = redoEntry[1];
            let value = redoEntry[2];

            if (type === 'pos') {
                // Store current position for undo before applying redo
                this.lastChangedObject.push([obj, 'pos']);
                obj.storeLastPosition(obj.getPosition());
                let currentPos = obj.getPosition();
                let delta = { x: value.x - currentPos.x, y: value.y - currentPos.y };
                obj.moveObject(delta.x, delta.y);
                obj.placeObject();
            } else if (type === 'scale') {
                // Store current scale for undo before applying redo
                this.lastChangedObject.push([obj, 'scale']);
                obj.storeLastScale();
                obj.updateObstructionScale(value);
            } else if (type === 'angle') {
                // Store current angle for undo before applying redo
                this.lastChangedObject.push([obj, 'angle']);
                obj.storeLastAngle();
                obj.rotateObstruction(value);
            }
        }
        this.studioStore.$patch({ 
            undoEnabled: this.lastChangedObject.length > 0,
            redoEnabled: this.redoStack.length > 0 
        });
    }
    removeSelectedObject() {
        if (this.currentSelectedObject instanceof Obstruction) {
            this.currentSelectedObject.removeObject();
        }

    }


    getDcSize() {
        let dcSize = 0;
        if (this.polygonModel && this.polygonModel.subarray) {
            dcSize += this.polygonModel.subarray.getDcSize();
        }
        return dcSize;
    }

    /**
     * Returns the number of visible (non-hidden, post-trim) panels in the scene.
     * This is the authoritative count — only panels actually rendered and
     * included in the design after any trimming.
     */
    getVisiblePanelCount() {
        if (this.polygonModel?.subarray) {
            return this.polygonModel.subarray.getNumberOfPanels();
        }
        return 0;
    }

    getGroundImage() {
        return this.groundImage;
    }

    getLatitude() {
        return this.latitude;
    }

    getLongitude() {
        return this.longitude;
    }

    // Assumes the obs lies fully within the polygon
    getAvailableArea() {
        const polArea = this.polygonModel.getAvailableArea();
        let obsArea = 0;
        this.polygonModel.getChildren().forEach((obstruction) => {
            if (obstruction instanceof Obstruction) {
                obsArea += obstruction.getAvailableArea();
            }
        });
        return polArea - obsArea;
    }

    switchTo2d() {
        if (!utils.isTouchDevice()) {
            this.lengthVisible = true;
            this.arcVisible = true;
        }
        if (this.controlsManager) this.controlsManager.use2dControls();
        this.cameraManager.useOrthographicCamera();
        this.rotateSceneBack();
        if (this.polygonModel) {
            this.polygonModel.switchTo2d();
            this.polygonModel.getChildren().forEach((obstruction) => {
                if (obstruction instanceof Obstruction) {
                    obstruction.switchTo2d();
                }
            });
        }
        // Also switch any obstructions parented directly to ground (placed on ground)
        if (this.ground) {
            this.ground.getChildren().forEach((child) => {
                if (child instanceof Obstruction) {
                    child.switchTo2d();
                }
            });
        }
        // Force world-matrix propagation after rotateSceneBack() so that
        // Box3.setFromObject() inside fitCameraToObject() reads the correct
        // XY-plane bounds (not the stale XZ-plane 3D bounds) → prevents black screen.
        this.sceneManager.scene.updateMatrixWorld(true);
        this.fitCameraToObject();
    }
    switchTo3d() {
        this.lengthVisible = false;
        this.arcVisible = false;
        if (this.controlsManager) this.controlsManager.use3dControls();
        this.cameraManager.usePerspectiveCamera();
        this.rotateScene();
        if (this.polygonModel) {
            this.polygonModel.switchTo3d();
            this.polygonModel.getChildren().forEach((obstruction) => {
                if (obstruction instanceof Obstruction) {
                    obstruction.switchTo3d();
                }
            });
        }
        // Also switch any obstructions parented directly to ground (placed on ground)
        if (this.ground) {
            this.ground.getChildren().forEach((child) => {
                if (child instanceof Obstruction) {
                    child.switchTo3d();
                }
            });
        }
    }
    setDesignId(designId) {
        this.designId = designId;
    }

    rotateScene() {
        if (!this.sceneRotated) {
            for (let child of this.sceneManager.scene.children) {
                child.rotateOnWorldAxis(new THREE.Vector3(1, 0, 0), -Math.PI / 2);
            }
            this.sceneRotated = true;
        }
    }

    rotateSceneBack() {
        if (this.sceneRotated) {
            for (let child of this.sceneManager.scene.children) {
                child.rotateOnWorldAxis(new THREE.Vector3(1, 0, 0), Math.PI / 2);
            }
            this.sceneRotated = false;
        }
    }

    updateCameraAzimuth(azimuth) {
        //store.commit(CONSTANTS.SET_CAMERA_AZIMUTH, azimuth);
    }

    addCameraUpdates(func) {
        if (this.cameraUpdates.indexOf(func) === -1) {
            this.cameraUpdates.push(func);
        }
    }

    appResize = () => {
        let resizeTimeout;
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(this.resizeRendererAndCamera, 500);
    };

    saveCanvasImage() {
        let canvas = document.getElementById('design-canvas');
        const dataURL = canvas.toDataURL();
        if (appMode.isAppMode()) {
            // APP MODE: store in studioDataService
            studioDataService.updateDesignData({ canvasImage: dataURL });
        } else {
            // WEB MODE: store in localStorage (original dev behavior)
            localStorage.setItem('canvasImage', dataURL);
        }
    }

    newPolygonModel(floorValue, isObstructionType = false) {
        this.floorValue = floorValue;
        this.studioStore.$patch({ undoEnabled: true });
        this.polygonModel = new PolygonModel(this, floorValue, isObstructionType);
        this.polygonModel.initDrawingMode();
        return this.polygonModel;
    }

    deselectOtherSelectedMeshes(objectToBeSelected, _polygonEnabled) {
        this.allMeshes = utils.getAllObjectMeshes(_polygonEnabled);
        let objects = [];
        this.allMeshes.forEach((mesh) => {
            if (mesh) objects.push(mesh.parent.container);
        })
        objects = objects.filter((object) => object !== objectToBeSelected);
        objects.forEach((object) => {
            object.deSelect();
        })
        this.currentSelectedObject = objectToBeSelected;
    }

    initDrawObstructions(obstructionType, height) {
        const drawObstruction = new Obstruction(this, height, obstructionType);
        if (obstructionType === DRAW_OBSTRUCTION) {
            drawObstruction.initDrawingMode();
        }
        else {
            drawObstruction.addObstructionInLocal();
            this.changeSlider(drawObstruction.scale);
            this.changeRotationSlider(drawObstruction.angle);
        }
        this.obstructionInScene();
    }

    // Clear undo/redo history - called when loading saved designs
    clearUndoRedoHistory() {
        this.lastChangedObject = [];
        this.redoStack = [];
        this.studioStore.$patch({ 
            undoEnabled: false,
            redoEnabled: false 
        });
    }

    // Called from UI when entering obstruction state to allow selecting/moving obstructions
    enableObstructionSelection() {
        if (this.dragControls && typeof this.dragControls.enableObstructions === 'function') {
            this.dragControls.enableObstructions();
        }
        this.obstructionSelectionEnabled = true;
    }
    // Called when entering roof drawing state to prevent selecting/moving obstructions
    disableObstructionSelection() {
        if (this.dragControls && typeof this.dragControls.disableObstructions === 'function') {
            this.dragControls.disableObstructions();
        }
        this.obstructionSelectionEnabled = false;
    }

    async loadGroundImage(groundImageData) {
        if (this.groundImage) {
            this.groundImage.remove();
        }
        this.groundImage = new GroundImage(this.stage, groundImageData, null, this);

        const aspectRatio = await getAspectRatio(groundImageData.url);

        const scale = scaleMetersToRatio(
            (groundImageData.scale === INVALID_SCALE) ?
                getDefaultGroundSize(this.stage).width :
                groundImageData.scale,
            aspectRatio,
            this.stage.getImageDimensions().width
        );

        this.groundImage.loadObject({
            scale,
            rotation: groundImageData.rotation,
            offset: (groundImageData.offset.length == 0) ? [0, 0] : groundImageData.offset,
        });
    }

    getImageDimensions() {
        return this.imageDimensions;
    }

    getWindowDims() {
        let isIphone = false;
        if (/iPhone|iPod/i.test(navigator.userAgent)) {
            isIphone = true;
        }
        let width, height
        if (isIphone) {
            width = window.outerWidth
            height = window.outerHeight
        } else {
            width = window.innerWidth
            height = window.innerHeight
        }
        // Subtract header height (arka-topbar) from height
        const headerEl = document.querySelector('.arka-topbar');
        const headerHeight = headerEl ? headerEl.offsetHeight : 0;
        height = height - headerHeight;
        return { width, height }
    }

    /* Resizes the canvas to the size of the window */
    resizeCanvas() {
        let windowDims = this.getWindowDims()
        this.canvas.width = windowDims.width;
        this.canvas.height = windowDims.height;
        this.resizeRendererAndCamera();
    }

    /* Resizing the renderer and camera. */
    // Resizes the canvas to the size of the window
    resizeRendererAndCamera = () => {
        //this.resizeCanvasToDisplaySize()
        let canvas = this.rendererManager.getDomElement();
        let parent = canvas;
        let parentBoundingRect = parent.getBoundingClientRect();
        let parentPadding = parseInt(Object(window.getComputedStyle(parent, null)).getPropertyValue("padding-left"));
        let windowDims = this.getWindowDims()
        this.screenDimensions = {
            width: windowDims.width - 2 * parentPadding,
            height: windowDims.height - 2 * parentPadding,
            left: parentBoundingRect.left,
            top: parentBoundingRect.top
        };
        this.rendererManager.resizeRenderer(this.screenDimensions);
        this.cameraManager.updateCamera(this.screenDimensions, this.imageDimensions);
    };

    destroyStage() {
        window.removeEventListener("resize", this.appResize, false);
        window.removeEventListener("scroll", this.appResize, false);
    }

    main() {
        this.resizeCanvas();
        this.resizeRendererAndCamera();
        this.animate();
    }

    getModelId() {
        this._MODEL_ID_INIT++;
        return this._MODEL_ID_INIT;
    }
    getObsId() {
        this._OBS_ID_INIT++;
        return this._OBS_ID_INIT;
    }

    getSubarrayId() {
        this._SUBARRAY_ID_INIT++;
        return this._SUBARRAY_ID_INIT;
    }

    getNormalisedZoom() {
        // returns normalised zoom level based on image dimension
        // scale followed is that for 300x300, zoom is 1 and linear scale
        // NOTE: camera's 'z' is 500. Changing might affect it. Not accounted.
        // NOTE: Image's aspect ratio is assumed to be 1:1
        return ((300 / this.imageDimensions.width) * this.cameraManager.camera.zoom);
    }

    changeSlider(val = 6) {
        const slider = document.getElementById("obstrSlider");
        // Set the value of the slider
        slider.ariaValueNow = val;
        slider.ariaValueText = val;
        slider.style.bottom = (val - 1) * (100 / 9) + '%';
        this.studioStore.$patch({ zoomValue: val });
        return true;
    }

    changeRotationSlider(value = 90) {
        // let selector = document.getElementById('selector')
        // let selectValue = document.getElementById('selectValue')
        // let progressBar = document.getElementById('progressBar')
        // selectValue.innerHTML = value+'&#xb0'
        // selector.style.left = (parseInt(value)/180)*100 + '%'
        // progressBar.style.width = (parseInt(value)/180)*100 + '%'

        let slider = document.getElementById("obstrRotationSlider");
        slider.ariaValueNow = value;
        slider.ariaValueText = value;
        slider.style.bottom = (value / 180) * 100 + '%';
        this.studioStore.$patch({ rotationValue: value });
        return true;
    }

    fitCameraToObject(object = this.ground.edgesMesh) {
        const box = new THREE.Box3().setFromObject(object);
        const orig_aspect_ratio = (this.cameraManager.orthographicCamera.right - this.cameraManager.orthographicCamera.left) / (this.cameraManager.orthographicCamera.top - this.cameraManager.orthographicCamera.bottom);

        let temAspectRatio = (box.max.x - box.min.x) / (box.max.y - box.min.y)

        let centerOnlyVertically = true

        if (centerOnlyVertically || temAspectRatio < orig_aspect_ratio) {
            // if (temAspectRatio < orig_aspect_ratio) {
            const adjusted_width = (box.max.y - box.min.y) * orig_aspect_ratio;

            this.cameraManager.orthographicCamera.top = box.max.y;
            this.cameraManager.orthographicCamera.bottom = box.min.y;
            this.cameraManager.orthographicCamera.left = -adjusted_width / 2;
            this.cameraManager.orthographicCamera.right = adjusted_width / 2;
        } else {
            const adjusted_height = (box.max.x - box.min.x) / orig_aspect_ratio;

            // To vertically center the template, this "sub" and the exact value -0.09 is required. 
            let sub = -(adjusted_height - (box.max.y - box.min.y)) / 2 - 0.09

            this.cameraManager.orthographicCamera.top = adjusted_height + sub;
            this.cameraManager.orthographicCamera.bottom = 0 + sub;
            this.cameraManager.orthographicCamera.left = box.min.x;
            this.cameraManager.orthographicCamera.right = box.max.x;

        }
        this.cameraManager.orthographicCamera.updateProjectionMatrix();
        //this.requestRender.bind(this)();
    }

    areOverlapping(selectedObject) {
        return isIntersecting(this.polygonModel.get2DVertices(), selectedObject.getVertices());
    }

    /**
     * Populate design_acsize and inverter_electrical_map on stageData.
     * Shared by both APP and WEB mode branches inside placePanels().
     * @param {Object} stageData - mutable stageData object (must already have panel_map set)
     */
    _populateAcSizeAndInverterMap(stageData) {
        const settings = studioDataService.getPanelSettings();
        const inverter = settings?.inverter ?? this.studioStore.selectedInverter;
        const inverterQty = settings?.inverterQuantity ?? this.studioStore.inverterQuantity ?? 1;

        // design_acsize: inverter_ac_power × quantity (kW)
        // Priority: max_ac_power_VA → max_ac_power_W → Size (kW) × 1000
        const acPowerVA = inverter?.max_ac_power_VA
            ?? inverter?.max_ac_power_W
            ?? (inverter?.Size != null ? inverter.Size * 1000 : null);
        stageData.design_acsize = acPowerVA != null
            ? parseFloat(((acPowerVA / 1000) * inverterQty).toFixed(3))
            : 0;

        // inverter_electrical_map
        stageData.inverter_electrical_map = this._buildInverterElectricalMap(
            inverter, inverterQty, stageData.panel_map
        );
    }

    /**
     * Build inverter electrical map matching the arka360 format.
     * Marketing portal does not place inverters in the scene or connect strings,
     * so strings[] inside each MPPT is always empty. MPPT metadata (stringRange,
     * maxStrings, suggestedStringCount) is computed from the inverter + panel specs.
     *
     * @param {Object|null} inverter  - selected inverter object from panelSettings / studioStore
     * @param {number}      quantity  - number of inverters
     * @param {Array|null}  panelMap  - stageData.panel_map (array of subarrayMaps)
     * @returns {Object} inverter electrical map
     */
    _buildInverterElectricalMap(inverter, quantity, panelMap) {
        if (!inverter) {
            return { micro: [], string: [], hybrid: [], central: [], isStringing: false };
        }

        // ── Collect linked subarray IDs and total visible panel count ──
        const linkedSubarraysIds = [];
        let totalPanels = 0;
        if (Array.isArray(panelMap)) {
            for (const subarray of panelMap) {
                if (!subarray?.id) continue;
                let subarrayPanelCount = 0;
                if (Array.isArray(subarray.rows)) {
                    for (const row of subarray.rows) {
                        if (!Array.isArray(row?.frames)) continue;
                        for (const frame of row.frames) {
                            if (frame.hidden) continue;
                            if (!Array.isArray(frame?.panels)) continue;
                            for (const panel of frame.panels) {
                                if (!panel.emptyPanel) subarrayPanelCount++;
                            }
                        }
                    }
                }
                if (subarrayPanelCount > 0) {
                    linkedSubarraysIds.push(subarray.id);
                    totalPanels += subarrayPanelCount;
                }
            }
        }

        // ── Estimate panel Voc for string range calculation ──
        // Use subarray's panel characteristics if available; fallback to defaults.
        const panelProps = this.polygonModel?.subarray?.panelProperties;
        const ch = panelProps?.characteristics || {};
        const cellNumber = ch.cell_number || 60;
        const cellType = (ch.cell_type || '').toLowerCase();
        // Voc per cell: ~0.7V mono, ~0.6V poly — gives a conservative Voc estimate
        const vocPerCell = cellType.includes('mono') ? 0.7 : 0.6;
        const estimatedVoc = cellNumber * vocPerCell;

        // ── Inverter MPPT parameters ──
        const mpptLowV = inverter.MPPT_Low_V ?? 0;
        const mpptHighV = inverter.MPPT_High_V ?? 0;
        const numMPPT = inverter.Number_of_MPPT ?? 1;

        // Parse Number_of_Strings (e.g. "2/2" → [2, 2] or "4/4/4/4/4/4" → [4,4,4,4,4,4])
        const stringsPerMPPT = typeof inverter.Number_of_Strings === 'string'
            ? inverter.Number_of_Strings.split('/').map(s => parseInt(s, 10) || 0)
            : [];

        // String range: min panels per string to reach MPPT_Low_V, max to stay under MPPT_High_V
        const stringRange = estimatedVoc > 0 ? {
            minimum: Math.max(1, Math.ceil(mpptLowV / estimatedVoc)),
            maximum: mpptHighV > 0 ? Math.floor(mpptHighV / estimatedVoc) : 1,
        } : { minimum: 1, maximum: 1 };

        // ── Distribute panels across MPPTs as suggestedStringCount ──
        // Optimal string length = midpoint of stringRange
        const optimalStringLen = Math.max(1, Math.round((stringRange.minimum + stringRange.maximum) / 2));
        const totalStringsNeeded = Math.ceil(totalPanels / optimalStringLen);
        // Total max strings across all MPPTs
        const totalMaxStrings = stringsPerMPPT.reduce((sum, v) => sum + v, 0) || numMPPT;

        const mppts = [];
        let stringsRemaining = totalStringsNeeded;
        for (let i = 0; i < numMPPT; i++) {
            const maxStrings = stringsPerMPPT[i] || Math.ceil(totalMaxStrings / numMPPT);
            const suggested = Math.min(maxStrings, Math.max(0, stringsRemaining));
            stringsRemaining -= suggested;
            mppts.push({
                strings: [],
                maxStrings,
                stringRange,
                linkedSubarraysIds: [...linkedSubarraysIds],
                suggestedStringCount: suggested,
            });
        }

        // ── Build full electricalProperties from the inverter object ──
        // Pass through all available inverter fields (mirrors API response shape)
        const elProps = inverter.electricalProperties || inverter;
        const electricalProperties = {
            id: elProps.id ?? null,
            image: elProps.image ?? null,
            deleted: elProps.deleted ?? false,
            deleted_at: elProps.deleted_at ?? null,
            Manufacturer: elProps.Manufacturer ?? inverter.brand ?? null,
            Make: elProps.Make ?? inverter.model ?? null,
            model: elProps.model ?? null,
            series: elProps.series ?? null,
            Source_link: elProps.Source_link ?? null,
            Note: elProps.Note ?? null,
            Size: elProps.Size ?? null,
            Number_of_MPPT: elProps.Number_of_MPPT ?? numMPPT,
            Number_of_Strings: elProps.Number_of_Strings ?? null,
            MPPT_Low_V: elProps.MPPT_Low_V ?? null,
            MPPT_High_V: elProps.MPPT_High_V ?? null,
            max_efficiency: elProps.max_efficiency ?? null,
            euro_efficiency: elProps.euro_efficiency ?? null,
            nominal_dc_voltage: elProps.nominal_dc_voltage ?? null,
            rated_dc_voltage: elProps.rated_dc_voltage ?? null,
            max_dc_voltage: elProps.max_dc_voltage ?? null,
            min_dc_voltage_to_start_feed_in: elProps.min_dc_voltage_to_start_feed_in ?? null,
            nominal_dc_power: elProps.nominal_dc_power ?? null,
            rated_dc_power: elProps.rated_dc_power ?? null,
            max_dc_power: elProps.max_dc_power ?? null,
            nominal_dc_current: elProps.nominal_dc_current ?? null,
            rated_dc_current: elProps.rated_dc_current ?? null,
            max_dc_current: elProps.max_dc_current ?? null,
            power_factor_min: elProps.power_factor_min ?? null,
            power_factor_max: elProps.power_factor_max ?? null,
            power_consumption_at_night: elProps.power_consumption_at_night ?? null,
            number_of_feed_in_phases: elProps.number_of_feed_in_phases ?? null,
            nominal_ac_voltage: elProps.nominal_ac_voltage ?? null,
            rated_ac_voltage: elProps.rated_ac_voltage ?? null,
            output_ac_voltage_range_end: elProps.output_ac_voltage_range_end ?? null,
            output_ac_voltage_range_start: elProps.output_ac_voltage_range_start ?? null,
            nominal_ac_power: elProps.nominal_ac_power ?? null,
            rated_ac_power: elProps.rated_ac_power ?? null,
            max_ac_power_VA: elProps.max_ac_power_VA ?? null,
            max_ac_power_W: elProps.max_ac_power_W ?? null,
            nominal_ac_current: elProps.nominal_ac_current ?? null,
            rated_ac_current: elProps.rated_ac_current ?? null,
            max_ac_current: elProps.max_ac_current ?? null,
            depth: elProps.depth ?? null,
            height: elProps.height ?? null,
            weight: elProps.weight ?? null,
            width: elProps.width ?? null,
            distortion: elProps.distortion ?? null,
            frequency: elProps.frequency ?? null,
            interface: elProps.interface ?? null,
            operation_temperature_end: elProps.operation_temperature_end ?? null,
            operation_temperature_start: elProps.operation_temperature_start ?? null,
            protection_class: elProps.protection_class ?? null,
            reference_id: elProps.reference_id ?? null,
            region: elProps.region ?? null,
            inverter_type: elProps.inverter_type ?? null,
            image_link: elProps.image_link ?? null,
            organisation_specific_inverter: elProps.organisation_specific_inverter ?? null,
        };

        const inverterEntry = {
            mppts,
            inverterMake: electricalProperties.Make,
            inverterDatabaseId: electricalProperties.id,
            electricalProperties,
            inverterManufacturer: electricalProperties.Manufacturer,
        };

        return {
            micro: [],
            hybrid: [],
            string: [inverterEntry],
            central: [],
            isStringing: false,
        };
    }

    /**
     * Trim excess panels to match target count from API report_json.
     * Hides tables from the last rows until panel count <= target.
     * If roof area is too small (placed < target), shows a notification.
     * @param {Subarray} subarray
     * @param {number} targetPanels
     */
    _trimPanelsToTarget(subarray, targetPanels) {
        const panelsPerTable = subarray.tableSizeUp * subarray.tableSizeWide;
        let totalPlaced = subarray.getNumberOfPanels();

        if (totalPlaced <= targetPanels) {
            if (totalPlaced < targetPanels) {
                const deficit = targetPanels - totalPlaced;
                console.warn(`[APP] Roof area insufficient — placed ${totalPlaced}/${targetPanels} panels (short by ${deficit})`);
                ElMessage({
                    message: `Roof area supports ${totalPlaced} panels (${deficit} short of recommended ${targetPanels}). Adjust roof drawing if needed.`,
                    type: 'warning',
                    duration: 6000,
                    showClose: true,
                });
            }
            return;
        }

        // Need to hide excess panels — iterate rows in reverse, tables in reverse
        // Using hideTable() instead of removeObject() to avoid saveState errors
        const rows = subarray.getChildren().slice().reverse();

        for (const row of rows) {
            if (totalPlaced <= targetPanels) break;

            const tables = row.getChildren().slice().reverse();
            for (const table of tables) {
                if (totalPlaced <= targetPanels) break;

                const tablePanelCount = table.getNumberOfPanels();
                if (tablePanelCount === 0) continue; // already hidden

                // Hide the table (sets hidden flag and hides all panels)
                table.hideTable();
                totalPlaced -= panelsPerTable;
            }
        }

        subarray.mergeGeometriesForAllPanels();

        const finalCount = subarray.getNumberOfPanels();
        if (finalCount > targetPanels) {
            console.warn(`[APP] Could not trim to exact target — final: ${finalCount}, target: ${targetPanels}`);
        }
    }

    async placePanels() {
        await this.polygonModel.onFillFace().then(async () => {
            // ── APP MODE: trim panels to match n_panels from report_json ──
            if (appMode.isAppMode() && this.appTargetPanels != null && this.polygonModel?.subarray) {
                this._trimPanelsToTarget(this.polygonModel.subarray, this.appTargetPanels);
                // Structures were built inside onFillFace() BEFORE trim, so they include
                // structure members for the now-hidden panels. Rebuild them using the
                // post-trim subarray — structureController skips hidden tables via
                // get3DBoundingBoxesExcludingHiddenTables(), producing a clean result.
                await this.polygonModel.rebuildStructures();
            } else if (appMode.isAppMode() && this.appTargetPanels == null) {
                // nPanels missing from report_json → keep all panels placed by fillFace (regular flow)
                console.log('[APP] nPanels not set in report_json — using full fillFace result (no trim)');
            }

            // ── Common: build panel_map, design sizes, and inverter electrical map ──
            const stageData = appMode.isAppMode()
                ? studioDataService.getStageData()
                : JSON.parse(localStorage.getItem('stageData'));

            stageData.availableArea = this.getAvailableArea();

            if (this.polygonModel?.subarray) {
                stageData.panel_map = [this.polygonModel.subarray.getSubarrayMap(false)];
            }

            stageData.design_dcsize = this.getDcSize();
            stageData.panelCount = this.getVisiblePanelCount();
            stageData.floorValue = this.floorValue;
            this._populateAcSizeAndInverterMap(stageData);

            if (appMode.isAppMode()) {
                // ── APP MODE: groundData, obstructions, snapshots, API save ──
                if (this.ground) {
                    stageData.groundData = this.ground.saveObject();
                }
                stageData.obstructionData = this.collectObstructionsFromScene();
                studioDataService.setStageData(stageData);

                // Attach the authoritative visible panel count to panelSettings
                // so it's included in the design_details payload sent to the API.
                const currentSettings = studioDataService.getPanelSettings();
                if (currentSettings) {
                    currentSettings.panelCount = stageData.panelCount;
                    studioDataService.setPanelSettings(currentSettings);
                }

                // Capture 2D and 3D snapshots, upload to Azure Blob Storage
                try {
                    const contactId = window.__arkaContactId ?? appMode.getContactId();
                    // Pre-check SAS token availability before capturing snapshots
                    await ensureSasToken();
                    const snapshots = await this.snapshotCapture.captureBothViews(2);
                    const { snapshot2DUrl, snapshot3DUrl } = await uploadSnapshots(snapshots, contactId);
                    stageData.snapshots = {
                        snapshot2DUrl,
                        snapshot3DUrl,
                        capturedAt: new Date().toISOString()
                    };
                    studioDataService.setStageData(stageData);
                } catch (snapshotError) {
                    console.warn('[APP] Snapshot upload skipped (SAS token or upload unavailable):', snapshotError?.message);
                }

                postToRN('PORTAL_SAVING');
                try {
                    await studioDataService.saveDesign();
                    console.log('[APP] ✅ Design saved successfully for contact:', window.__arkaContactId ?? appMode.getContactId());
                    postToRN('PORTAL_SAVED', {
                        contactId: window.__arkaContactId ?? appMode.getContactId(),
                    });
                } catch (error) {
                    console.error('[APP] ❌ Failed to save design:', error?.message);
                    postToRN('PORTAL_SAVE_FAILED', {
                        error: error?.message ?? 'Save failed. Please try again.',
                    });
                }
            } else {
                // ── WEB MODE: persist to localStorage ──
                localStorage.setItem('stageData', JSON.stringify(stageData));
            }

            return Promise.resolve(true);
        }, (error) => Promise.reject(error));
    }
    
    collectObstructionsFromScene() {
        const obstructions = [];
        const collect = (children) => {
            children.forEach(child => {
                if (child instanceof Obstruction) obstructions.push(child.saveObject());
            });
        };
        if (this.ground) collect(this.ground.getChildren());
        if (this.polygonModel) collect(this.polygonModel.getChildren());
        return obstructions;
    }

    removePanels() {
        if (this.polygonModel?.subarray) {
            this.polygonModel.subarray.removeObject();
        }
        if (this.polygonModel?.structures) {
            this.polygonModel.structures.clear();
        }
    }

    panelMapExporter() {
        let panelMapArray = [];
        if (this.polygonModel && this.polygonModel.subarray) {
            panelMapArray.push(this.polygonModel.subarray.getSubarrayMap(true));
        }
        return panelMapArray;
    }
    
    roofMapExporter() {
        const allModels = utils.getAllModelType()
        utils.getModels(stage.ground, allModels, true);
        
        const roofMap =  {
            polygons: [],
            obstructions: [],
            ground: [],
        }
        
        //for ground
        let ground = {
            height: 0,
            width: 0,
            vertices: [],
        }
        ground.height = stage.getImageDimensions().height;
        ground.width = stage.getImageDimensions().width;
        ground.vertices = stage.ground.get2DVertices();
        roofMap.ground.push(ground);
        
        for (let polygonModel of allModels.polygons) {
            let polygon = {
                id: polygonModel.id,
                placable: true,
                ignored: false,
                edges: []
            };
            let vertices = polygonModel.get3DVertices();
            let nVertices = vertices.length;
            for (let i = 0; i < nVertices; i++) {
                polygon.edges.push({
                    heightParapet: 0,
                    thicknessParapet: 0,
                    setbackInside: 0,
                    setbackOutside: 0,
                    points: [
                        vertices[i],
                        vertices[(i + 1) % nVertices]  // This wraps around to 0 when i+1 equals nVertices
                    ],
                });
            }
            roofMap.polygons.push(polygon)
        }
        for (let obstruction of allModels.obstructions) {
            let obs = {
                id: obstruction.id,
                placable: false,
                ignored: false,
                edges: []
            };
            let vertices = obstruction.get3DVertices();
            let nVertices = vertices.length;
            for (let i = 0; i < nVertices; i++) {
                obs.edges.push({
                    heightParapet: 0,
                    thicknessParapet: 0,
                    setbackInside: 0,
                    setbackOutside: 0,
                    points: [
                        vertices[i],
                        vertices[(i + 1) % nVertices]  // This wraps around to 0 when i+1 equals nVertices
                    ],
                });
            }
            roofMap.obstructions.push(obs)
        }
        return roofMap;
    }

    roofMapExporter() {
        const allModels = utils.getAllModelType()
        utils.getModels(stage.ground, allModels, true);

        const roofMap = {
            polygons: [],
            obstructions: [],
            ground: [],
        }

        //for ground
        let ground = {
            height: 0,
            width: 0,
            vertices: [],
        }
        ground.height = stage.getImageDimensions().height;
        ground.width = stage.getImageDimensions().width;
        ground.vertices = stage.ground.get2DVertices();
        roofMap.ground.push(ground);

        for (let polygonModel of allModels.polygons) {
            let polygon = {
                id: polygonModel.id,
                placable: true,
                ignored: false,
                edges: []
            };
            let vertices = polygonModel.get3DVertices();
            let nVertices = vertices.length;
            for (let i = 0; i < nVertices; i++) {
                polygon.edges.push({
                    heightParapet: 0,
                    thicknessParapet: 0,
                    setbackInside: 0,
                    setbackOutside: 0,
                    points: [
                        vertices[i],
                        vertices[(i + 1) % nVertices]  // This wraps around to 0 when i+1 equals nVertices
                    ],
                });
            }
            roofMap.polygons.push(polygon)
        }
        for (let obstruction of allModels.obstructions) {
            let obs = {
                id: obstruction.id,
                placable: false,
                ignored: false,
                edges: []
            };
            let vertices = obstruction.get3DVertices();
            let nVertices = vertices.length;
            for (let i = 0; i < nVertices; i++) {
                obs.edges.push({
                    heightParapet: 0,
                    thicknessParapet: 0,
                    setbackInside: 0,
                    setbackOutside: 0,
                    points: [
                        vertices[i],
                        vertices[(i + 1) % nVertices]  // This wraps around to 0 when i+1 equals nVertices
                    ],
                });
            }
            roofMap.obstructions.push(obs)
        }
        return roofMap;
    }

    /**
     * Calls API to get the Solar Access and Irradiance Map.
     * leadId is optional — the API accepts an empty string gracefully.
     * In app mode the contactId is used as the leadId so solar data is
     * always fetched regardless of whether componentData.savingsData exists.
     */
    async fetchIrradianceAndAccess() {
        // Request token to avoid applying stale results after rapid navigation
        this.heatSolar = this.heatSolar || {};
        this.heatSolar._reqId = (this.heatSolar._reqId || 0) + 1;
        const myReq = this.heatSolar._reqId;

        let leadId = '';

        if (appMode.isAppMode()) {
            // App mode: use contactId as the leadId identifier
            leadId = appMode.getContactId() || '';
        } else {
            // Web mode: try to read leadId from localStorage componentData
            try {
                const raw = localStorage.getItem('componentData');
                const cd  = raw ? JSON.parse(raw) : null;
                if (cd?.savingsData?.leadId) leadId = cd.savingsData.leadId;
            } catch (_) { /* ignore parse errors */ }
        }

        this.heatSolar.apiLoading = true;

        const postData = {
            roofMap:         this.roofMapExporter(),
            panelMap:        this.panelMapExporter(),
            latitude:        this.latitude,
            longitude:       this.longitude,
            imageDimensions: this.imageDimensions,
            leadId,
        };

        try {
            const response = await API.HEAT_SOLAR.FETCH_HEAT_SOLAR(postData);
            // If another request started after this one, ignore this result
            if (myReq !== this.heatSolar._reqId) return;
            if (response.data.heat_map)    this.heatSolar.heatMap    = response.data.heat_map;
            if (response.data.solar_access) this.heatSolar.solarAccess = response.data.solar_access;
            ElMessage({ message: 'Solar data loaded successfully', type: 'success' });
        } catch (error) {
            console.error('[APP] fetchIrradianceAndAccess failed:', error);
            ElMessage({ message: 'Failed to load Solar Access / Irradiance Map', type: 'error' });
        } finally {
            // If another request started after this one, ignore finalize side-effects
            if (myReq === this.heatSolar._reqId) {
                this.heatSolar.apiLoading = false;
                if (this.heatSolar.solarAccess) {
                    HeatSolar._updateSubarraySolarAccess(this.ground, this.heatSolar.solarAccess);
                }
            }
        }
    }

    clearIrradianceAndAccess() {
        // Hide overlays and clear any generated textures/meshes
        try { this.updateSolarAccess(false); } catch(_) {}
        try { this.updateIrradianceMap(false); } catch(_) {}
        if (this.polygonModel) {
            try { this.polygonModel.removeRoofTexture(); } catch(_) {}
        }
    }

    /**
     * Positions the 3-D perspective camera to frame the entire design.
     * Uses the scene's 3D bounding sphere so the view is correct for
     * any building size — short or tall, small or large footprint.
     * Must be called AFTER switchTo3d() so the scene is already rotated.
     */
    fitCameraToDesign3D() {
        if (!this.sceneRotated || !this.cameraManager?.perspectiveCamera) return;

        // Build axis-aligned bbox over all visible meshes
        const bbox = new THREE.Box3();
        this.sceneManager.scene.traverse(obj => {
            if (obj.isMesh && obj.visible) bbox.expandByObject(obj);
        });
        if (bbox.isEmpty()) return;

        const center = new THREE.Vector3();
        const size   = new THREE.Vector3();
        bbox.getCenter(center);
        bbox.getSize(size);

        // Bounding sphere radius covers the full scene diagonally
        const bsphere = new THREE.Sphere();
        bbox.getBoundingSphere(bsphere);
        const radius = bsphere.radius;

        // Distance needed so the sphere fits inside the FOV (with 1.8× comfort buffer)
        const fovRad = this.cameraManager.perspectiveCamera.fov * (Math.PI / 180);
        const dist   = (radius / Math.sin(fovRad / 2)) * 1.8;

        // Place camera above-and-behind at ~35° elevation (good balance for flat roofs)
        const elevRad = 35 * (Math.PI / 180);
        const cam = new THREE.Vector3(
            center.x,
            center.y + dist * Math.sin(elevRad),
            center.z + dist * Math.cos(elevRad),
        );

        this.cameraManager.perspectiveCamera.position.copy(cam);
        this.cameraManager.perspectiveCamera.lookAt(center);
        this.cameraManager.perspectiveCamera.updateProjectionMatrix();

        // Sync orbit controls so user can immediately pan/rotate from this position
        if (this.controlsManager?._3dControls) {
            this.controlsManager._3dControls.target.copy(center);
            this.controlsManager._3dControls.update();
        }
    }

    getSolarAccessColorMap() {
        return this.solarAccessColorMap;
    }

    updateSolarAccess(visibility) {
        if (visibility) this.heatSolar.showSolarAccess();
        else this.heatSolar.hideSolarAccess()
    }

    updateIrradianceMap(visibility) {
        if (visibility) this.heatSolar.showHeatMap()
        else this.heatSolar.hideHeatMap()
    }
}
